import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createHighlightFolder,
  deleteHighlight,
  deleteHighlightFolder,
  downloadHighlightFile,
  listClips,
  listClipRuleRuns,
  listHighlightFolders,
  listHighlights,
  patchHighlight,
  publishHighlight,
  renameHighlightFolder,
  type Clip,
  type Highlight,
  type HighlightFolder,
} from '../api/clips';
import { CreateClipWizard } from '../components/CreateClipWizard';
import { ReloadableVideo } from '../components/ClipPlayer';
import { HoverPreview } from '../components/HoverPreview';
import { eventGameClock, eventLabel, formatClipClock } from '../lib/clipLabels';

type FolderScope = 'all' | 'inbox' | string;
type StatusFilter = 'all' | 'ready' | 'processing' | 'failed' | 'review';
type WhenFilter = 'all' | '7d' | '30d';
type SortKey = 'newest' | 'oldest' | 'name' | 'longest';

export function GeneratedVideosPage() {
  const qc = useQueryClient();
  const [playing, setPlaying] = useState<Highlight | null>(null);
  const [folder, setFolder] = useState<FolderScope>('all');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [when, setWhen] = useState<WhenFilter>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [newFolder, setNewFolder] = useState('');
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  const listQuery = useQuery({
    queryKey: ['highlights'],
    queryFn: listHighlights,
    refetchInterval: (q) => {
      const rows = q.state.data ?? [];
      return rows.some((r) => r.render_status === 'queued' || r.render_status === 'running') ? 1500 : 15_000;
    },
  });
  const foldersQuery = useQuery({
    queryKey: ['highlight-folders'],
    queryFn: listHighlightFolders,
  });

  const videos = listQuery.data ?? [];
  const folders = foldersQuery.data ?? [];
  const inboxCount = videos.filter((v) => !v.folder_id).length;
  const playingLive = playing ? videos.find((v) => v.id === playing.id) ?? playing : null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const rows = videos.filter((v) => {
      if (folder === 'inbox' && v.folder_id) return false;
      if (folder !== 'all' && folder !== 'inbox' && v.folder_id !== folder) return false;
      if (status === 'ready' && v.render_status !== 'ready') return false;
      if (status === 'failed' && v.render_status !== 'failed') return false;
      if (status === 'processing' && v.render_status !== 'queued' && v.render_status !== 'running') return false;
      if (status === 'review' && v.review_status !== 'pending_review') return false;
      if (q && !v.name.toLowerCase().includes(q) && !(v.description ?? '').toLowerCase().includes(q)) return false;
      if (when !== 'all') {
        const t = new Date(v.created_at).getTime();
        const days = when === '7d' ? 7 : 30;
        if (now - t > days * 86400000) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'longest') return b.total_duration_ms - a.total_duration_ms;
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sort === 'oldest' ? da - db : db - da;
    });
    return rows;
  }, [videos, folder, search, status, when, sort]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['highlights'] });
    qc.invalidateQueries({ queryKey: ['highlight-folders'] });
  };

  const remove = useMutation({
    mutationFn: deleteHighlight,
    onSuccess: (_, id) => {
      if (playing?.id === id) setPlaying(null);
      invalidate();
    },
  });
  const move = useMutation({
    mutationFn: ({ id, folder_id }: { id: string; folder_id: string | null }) =>
      patchHighlight(id, { folder_id }),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    mutationFn: publishHighlight,
    onSuccess: invalidate,
  });
  const addFolder = useMutation({
    mutationFn: (name: string) => createHighlightFolder(name),
    onSuccess: (f) => {
      setNewFolder('');
      setFolder(f.id);
      invalidate();
    },
  });
  const saveFolderName = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameHighlightFolder(id, name),
    onSuccess: () => {
      setRenameId(null);
      invalidate();
    },
  });
  const dropFolder = useMutation({
    mutationFn: deleteHighlightFolder,
    onSuccess: (_, id) => {
      if (folder === id) setFolder('all');
      invalidate();
    },
  });

  // Runs history - surfaces "why didn't my auto-highlight generate?" without
  // making the user hunt through logs. Empty state is hidden entirely.
  const runsQuery = useQuery({
    queryKey: ['clip-rule-runs'],
    queryFn: () => listClipRuleRuns(),
    refetchInterval: 30_000,
  });
  const skippedRuns = (runsQuery.data ?? []).filter((r) => r.status !== 'ok').slice(0, 5);

  const filtersOn = search !== '' || status !== 'all' || when !== 'all' || sort !== 'newest';
  const activeFolderName = folder === 'all'
    ? 'All videos'
    : folder === 'inbox'
      ? 'Unsorted'
      : folders.find((f) => f.id === folder)?.name ?? 'Folder';

  return (
    <div className="flex h-full min-h-0 flex-col px-4 py-4">
      <div className="page-head">
        <p className="page-sub">Play, download, and file finished videos.</p>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => setWizardOpen(true)}>
            Create highlight
          </button>
          <Link to="/clips" className="btn-primary">Stitch from clips</Link>
        </div>
      </div>

      {skippedRuns.length > 0 && (
        <div className="mb-3 rounded-lg bg-warn/10 px-3 py-2 text-[13px] text-amber-100 ring-1 ring-warn/25">
          <div className="mb-1 font-semibold">
            {skippedRuns.length} recent auto-highlight run{skippedRuns.length === 1 ? '' : 's'} did not generate a video:
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {skippedRuns.map((r) => (
              <li key={r.id}>
                <span className="font-mono text-[10px]">{r.created_at.slice(0, 16).replace('T', ' ')}</span>
                {' - '}
                <span className="text-white">{r.rule_name || 'rule'}</span>
                {r.reason && <>: <span>{r.reason}</span></>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-4">
        <aside className="card flex w-56 shrink-0 flex-col p-2">
          <div className="px-2 pb-2 pt-1 text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">
            Folders
          </div>
          <FolderBtn
            active={folder === 'all'}
            label="All videos"
            count={videos.length}
            onClick={() => setFolder('all')}
          />
          <FolderBtn
            active={folder === 'inbox'}
            label="Unsorted"
            count={inboxCount}
            onClick={() => setFolder('inbox')}
          />
          <div className="my-2 h-px bg-white/5" />
          <div className="min-h-0 flex-1 space-y-0.5 overflow-auto">
            {folders.map((f) => (
              <div key={f.id} className="group relative">
                {renameId === f.id ? (
                  <form
                    className="px-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = renameVal.trim();
                      if (name) saveFolderName.mutate({ id: f.id, name });
                    }}
                  >
                    <input
                      className="input py-1.5 text-xs"
                      value={renameVal}
                      onChange={(e) => setRenameVal(e.target.value)}
                      autoFocus
                      onBlur={() => setRenameId(null)}
                    />
                  </form>
                ) : (
                  <FolderBtn
                    active={folder === f.id}
                    label={f.name}
                    count={f.video_count}
                    onClick={() => setFolder(f.id)}
                    onRename={() => { setRenameId(f.id); setRenameVal(f.name); }}
                    onDelete={() => {
                      if (confirm(`Delete folder “${f.name}”? Videos stay in Unsorted.`)) {
                        dropFolder.mutate(f.id);
                      }
                    }}
                  />
                )}
              </div>
            ))}
            {folders.length === 0 && (
              <p className="px-2 py-3 text-[11px] leading-snug text-subtle">
                Make a folder for a campaign, match week, or client cutdown.
              </p>
            )}
          </div>
          <form
            className="mt-2 flex gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              const name = newFolder.trim();
              if (name) addFolder.mutate(name);
            }}
          >
            <input
              className="input py-1.5 text-xs"
              placeholder="New folder"
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
            />
            <button type="submit" className="btn-secondary px-2 py-1.5 text-xs" disabled={!newFolder.trim()}>
              +
            </button>
          </form>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <label>
              <span className="label">Search</span>
              <input
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or note"
              />
            </label>
            <Select
              label="Status"
              value={status}
              onChange={(v) => setStatus(v as StatusFilter)}
              options={[
                { id: 'all', label: 'Any status' },
                { id: 'ready', label: 'Ready' },
                { id: 'processing', label: 'Processing' },
                { id: 'failed', label: 'Failed' },
                { id: 'review', label: 'Needs review' },
              ]}
            />
            <Select
              label="Created"
              value={when}
              onChange={(v) => setWhen(v as WhenFilter)}
              options={[
                { id: 'all', label: 'Any time' },
                { id: '7d', label: 'Last 7 days' },
                { id: '30d', label: 'Last 30 days' },
              ]}
            />
            <Select
              label="Sort"
              value={sort}
              onChange={(v) => setSort(v as SortKey)}
              options={[
                { id: 'newest', label: 'Newest' },
                { id: 'oldest', label: 'Oldest' },
                { id: 'name', label: 'Name' },
                { id: 'longest', label: 'Longest' },
              ]}
            />
          </div>
          <div className="mb-3 flex items-center justify-between text-xs text-subtle">
            <span>
              {activeFolderName}
              {' · '}
              {filtered.length} {filtered.length === 1 ? 'video' : 'videos'}
            </span>
            {filtersOn && (
              <button
                type="button"
                className="btn-secondary py-1 text-xs"
                onClick={() => { setSearch(''); setStatus('all'); setWhen('all'); setSort('newest'); }}
              >
                Reset filters
              </button>
            )}
          </div>

          {listQuery.isError ? (
            <div className="alert-bad grid flex-1 place-items-center">
              Could not load videos. Refresh the page.
            </div>
          ) : videos.length === 0 && !listQuery.isLoading ? (
            <div className="card grid flex-1 place-items-center">
              <div className="max-w-sm px-6 py-10 text-center">
                <div className="text-[15px] font-semibold text-white">No videos yet</div>
                <p className="mt-2 text-[13px] text-subtle">
                  Build a timeline on Clips, then Create video. It will land here.
                </p>
                <Link to="/clips" className="btn-primary mt-4">Open clips</Link>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty card grid flex-1 place-items-center">
              Nothing matches these filters.
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto pr-1">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filtered.map((r) => (
                  <VideoCard
                    key={r.id}
                    reel={r}
                    folders={folders}
                    playing={playing?.id === r.id}
                    onPlay={() => setPlaying(r)}
                    onMove={(folderId) => move.mutate({ id: r.id, folder_id: folderId })}
                    onPublish={() => publish.mutate(r.id)}
                    onDelete={() => {
                      if (confirm('Delete this video?')) remove.mutate(r.id);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {playingLive && (
        <VideoOffcanvas
          reel={playingLive}
          folders={folders}
          onClose={() => setPlaying(null)}
          onMove={(folderId) => move.mutate({ id: playingLive.id, folder_id: folderId })}
        />
      )}

      <CreateClipWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onQueued={() => {
          qc.invalidateQueries({ queryKey: ['highlights'] });
          qc.invalidateQueries({ queryKey: ['highlight-folders'] });
        }}
      />
    </div>
  );
}

function VideoOffcanvas({
  reel, folders, onClose, onMove,
}: {
  reel: Highlight;
  folders: HighlightFolder[];
  onClose: () => void;
  onMove: (folderId: string | null) => void;
}) {
  const src = reel.preview_url || reel.download_url || undefined;
  const ready = reel.render_status === 'ready' && !!src;
  const folderName = folders.find((f) => f.id === reel.folder_id)?.name ?? 'Unsorted';
  const clipsQuery = useQuery({
    queryKey: ['clips', 'offcanvas'],
    queryFn: () => listClips({ limit: 200 }),
  });
  const clips = useMemo(() => {
    const byId = new Map((clipsQuery.data ?? []).map((c) => [c.id, c]));
    return reel.clip_ids.map((id) => byId.get(id)).filter((c): c is Clip => !!c);
  }, [clipsQuery.data, reel.clip_ids]);

  const [entered, setEntered] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const requestClose = () => {
    if (exiting) return;
    setExiting(true);
    setEntered(false);
  };

  useEffect(() => {
    if (!exiting) return;
    const t = window.setTimeout(onClose, 340);
    return () => window.clearTimeout(t);
  }, [exiting, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const open = entered && !exiting;

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        className={`offcanvas-backdrop absolute inset-0 bg-black/55 backdrop-blur-[2px] ${open ? 'is-open' : ''}`}
        aria-label="Close video"
        onClick={requestClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="video-offcanvas-title"
        className={`offcanvas-panel absolute inset-y-0 right-0 flex w-full max-w-[28rem] flex-col bg-surface ring-1 ring-white/[0.06] sm:max-w-[32rem] ${open ? 'is-open' : ''}`}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-white/[0.06] px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">Video</div>
            <h2 id="video-offcanvas-title" className="mt-0.5 truncate text-[15px] font-semibold text-white">
              {reel.name}
            </h2>
          </div>
          <button type="button" className="btn-secondary px-2.5 py-1.5 text-xs" onClick={requestClose}>
            Close
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          <div className={`bg-black ${open ? 'offcanvas-rise' : ''}`}>
            {ready && src ? (
              <ReloadableVideo
                src={src}
                autoPlay
                className="aspect-video w-full bg-black"
              />
            ) : (
              <div className="grid aspect-video place-items-center text-sm text-subtle">
                {reel.render_status === 'failed' ? 'Render failed' : 'Video is still processing…'}
              </div>
            )}
          </div>

          <div className="space-y-5 p-4">
            {reel.description && (
              <p className={`text-sm leading-relaxed text-slate-300 ${open ? 'offcanvas-rise' : ''}`} style={{ animationDelay: '60ms' }}>
                {reel.description}
              </p>
            )}

            <dl className={`grid grid-cols-2 gap-2 ${open ? 'offcanvas-rise' : ''}`} style={{ animationDelay: '90ms' }}>
              <Meta label="Duration" value={formatClipClock(reel.total_duration_ms)} />
              <Meta label="Clips" value={String(reel.clip_ids.length)} />
              <Meta label="Size" value={formatBytes(reel.output_size_bytes)} />
              <Meta label="Folder" value={folderName} />
              <Meta label="Created" value={formatWhen(reel.created_at)} />
              <Meta label="Ready" value={reel.output_ready_at ? formatWhen(reel.output_ready_at) : '-'} />
            </dl>

            <label className={`block text-xs text-subtle ${open ? 'offcanvas-rise' : ''}`} style={{ animationDelay: '130ms' }}>
              Move to folder
              <select
                className="select mt-1"
                value={reel.folder_id ?? ''}
                onChange={(e) => onMove(e.target.value || null)}
              >
                <option value="">Unsorted</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </label>

            <div className={open ? 'offcanvas-rise' : ''} style={{ animationDelay: '170ms' }}>
              <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">
                In this video
              </div>
              {clipsQuery.isLoading && (
                <p className="text-xs text-subtle">Loading clips…</p>
              )}
              {!clipsQuery.isLoading && clips.length === 0 && (
                <p className="text-xs text-subtle">Clip details are not available.</p>
              )}
              <ol className="space-y-2">
                {clips.map((c, i) => (
                  <li
                    key={c.id}
                    className={`flex gap-2 rounded-xl bg-elevated p-2 ring-1 ring-white/[0.06] ${open ? 'offcanvas-rise' : ''}`}
                    style={{ animationDelay: `${200 + i * 45}ms` }}
                  >
                    <div className="relative h-14 w-[5.5rem] shrink-0 overflow-hidden rounded-lg bg-black">
                      <HoverPreview src={c.preview_url} poster={c.preview_thumb_url} />
                      <span className="pointer-events-none absolute left-1 top-1 rounded bg-black/70 px-1 text-[9px] font-bold text-white">
                        {i + 1}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-white">
                        {eventLabel(c.event_type)}
                        {c.person_name ? ` · ${c.person_name}` : ''}
                      </div>
                      <div className="truncate text-[11px] text-subtle">
                        {[c.team_name, c.competition_code, minuteLabel(c)].filter(Boolean).join(' · ')}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        {formatClipClock(c.duration_ms)}
                        {c.rating ? ` · ${c.rating}★` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <footer className="flex shrink-0 gap-2 border-t border-white/[0.06] p-3">
          <DownloadBtn reel={reel} className="flex-1" />
          <Link to="/clips" className="btn-secondary flex-1 text-xs">
            Open clips
          </Link>
        </footer>
      </aside>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-elevated px-3 py-2 ring-1 ring-white/[0.06]">
      <dt className="text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">{label}</dt>
      <dd className="mt-0.5 truncate text-sm font-medium text-white">{value}</dd>
    </div>
  );
}

function formatBytes(n: number | null) {
  if (!n || n <= 0) return '-';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function minuteLabel(c: Clip) {
  const clock = eventGameClock(c);
  if (!clock) return c.period || '';
  return c.period ? `${clock} ${c.period}` : clock;
}

function FolderBtn({
  active, label, count, onClick, onRename, onDelete,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className={`flex items-center rounded-lg ${active ? 'bg-white/[0.07]' : 'hover:bg-white/5'}`}>
      <button
        type="button"
        onClick={onClick}
        className={`min-w-0 flex-1 px-3 py-2 text-left ${active ? 'text-white' : 'text-slate-300'}`}
      >
        <span className="block truncate text-sm font-medium">{label}</span>
        <span className="block text-[10px] text-subtle">{count}</span>
      </button>
      {onRename && onDelete && (
        <div className="flex shrink-0 pr-1 opacity-0 transition group-hover:opacity-100">
          <button type="button" className="rounded px-1 text-[10px] text-subtle hover:text-white" onClick={onRename} title="Rename">
            ✎
          </button>
          <button type="button" className="rounded px-1 text-[10px] text-rose-200 hover:text-rose-100" onClick={onDelete} title="Delete">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function VideoCard({
  reel, folders, playing, onPlay, onMove, onPublish, onDelete,
}: {
  reel: Highlight;
  folders: HighlightFolder[];
  playing: boolean;
  onPlay: () => void;
  onMove: (folderId: string | null) => void;
  onPublish: () => void;
  onDelete: () => void;
}) {
  const ready = reel.render_status === 'ready' && !!(reel.preview_url || reel.download_url);
  const pending = reel.render_status === 'queued' || reel.render_status === 'running';
  const failed = reel.render_status === 'failed';
  const folderName = folders.find((f) => f.id === reel.folder_id)?.name;

  return (
    <article className={`card overflow-hidden transition ${
      playing ? 'ring-white/20' : 'hover:ring-white/15'
    }`}>
      <button
        type="button"
        onClick={() => { if (ready) onPlay(); }}
        className="relative block aspect-video w-full overflow-hidden bg-black"
        disabled={!ready}
      >
        {ready ? (
          <HoverPreview
            src={reel.preview_url || reel.download_url}
            poster={reel.poster_url}
          />
        ) : reel.poster_url ? (
          <img src={reel.poster_url} alt="" className={`h-full w-full object-cover ${pending ? 'opacity-40' : 'opacity-50'}`} />
        ) : (
          <div className="grid h-full place-items-center text-sm text-subtle">
            {failed ? 'Failed' : 'Preparing…'}
          </div>
        )}
        {pending && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-accent" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-white">
              {reel.render_status === 'running' ? 'Processing…' : 'Pending…'}
            </span>
            <div className="w-40">
              <div className="h-1.5 overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full bg-accent transition-[width] duration-500"
                  style={{ width: `${Math.max(4, Math.min(100, reel.render_progress ?? 0))}%` }}
                />
              </div>
              <div className="mt-1 text-center text-[10px] text-white/80">
                {reel.render_stage || (reel.render_status === 'running' ? 'Encoding' : 'Waiting')}
                {typeof reel.render_progress === 'number' ? ` · ${reel.render_progress}%` : ''}
              </div>
            </div>
            {reel.render_started_at && (
              <span className="text-[10px] text-white/70">
                Started {new Date(reel.render_started_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        )}
        {failed && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-rose-100">Failed</span>
          </div>
        )}
        <span className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {formatClipClock(reel.total_duration_ms)}
        </span>
        <StatusPill status={reel.render_status} />
      </button>
      <div className="space-y-2 p-3">
        <div>
          <div className="truncate font-semibold text-white">{reel.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-subtle">
            <span>{reel.clip_ids.length} clips</span>
            <span>·</span>
            <span>{new Date(reel.created_at).toLocaleDateString()}</span>
            {(reel.render_finished_at || reel.output_ready_at) && ready && (
              <>
                <span>·</span>
                <span>
                  Finished {new Date(reel.render_finished_at || reel.output_ready_at || '').toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                </span>
              </>
            )}
            {reel.aspect && reel.aspect !== '16:9' && (
              <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300 ring-1 ring-white/10">
                {reel.aspect}
              </span>
            )}
            {ready && (
              <span className="rounded-full bg-emerald-400/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-100 ring-1 ring-emerald-300/30">
                File ready
              </span>
            )}
            {reel.review_status === 'pending_review' && (
              <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-100 ring-1 ring-amber-300/30">
                Needs Publish
              </span>
            )}
            {reel.review_status === 'published' && ready && (
              <span className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent ring-1 ring-accent/25">
                Live on site
              </span>
            )}
            {reel.review_status !== 'pending_review' && reel.folder_id && !ready && (
              <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300 ring-1 ring-white/10">
                Live when ready
              </span>
            )}
            {folderName && (
              <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300 ring-1 ring-white/10">
                {folderName}
              </span>
            )}
          </div>
          {reel.render_error && !ready && (
            <p className="mt-1 line-clamp-2 text-xs text-rose-300">{reel.render_error}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {reel.review_status === 'pending_review' && (
            <button type="button" className="btn-primary flex-1 text-xs" onClick={onPublish}>
              Publish
            </button>
          )}
          <button type="button" className="btn-secondary flex-1 text-xs" onClick={onPlay} disabled={!ready}>
            Play
          </button>
          <DownloadBtn reel={reel} />
          <select
            className="select max-w-[7.5rem] py-1.5 text-[11px]"
            value={reel.folder_id ?? ''}
            onChange={(e) => onMove(e.target.value || null)}
            title="Move to folder"
          >
            <option value="">Unsorted</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <button type="button" className="btn-secondary px-2 text-xs text-rose-200" onClick={onDelete} title="Delete">
            ×
          </button>
        </div>
      </div>
    </article>
  );
}

function DownloadBtn({ reel, className }: { reel: Highlight; className?: string }) {
  const [busy, setBusy] = useState(false);
  const ready = reel.render_status === 'ready';
  return (
    <button
      type="button"
      className={`btn-primary text-xs ${className ?? ''}`}
      disabled={!ready || busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadHighlightFile(reel.id, reel.name);
        } catch (err) {
          alert(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? '…' : 'Download'}
    </button>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls = status === 'ready'
    ? 'bg-emerald-500/80 text-white'
    : status === 'failed'
      ? 'bg-rose-500/80 text-white'
      : 'bg-amber-400/90 text-black';
  const label = status === 'ready' ? 'Ready' : status === 'failed' ? 'Error' : status === 'running' ? 'Processing' : 'Pending';
  return (
    <span className={`pointer-events-none absolute left-2 top-2 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>
      {label}
    </span>
  );
}

function Select({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; label: string }>;
}) {
  return (
    <label>
      <span className="label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="select">
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
